/**
 * Secure Access sign-in lifetime policy (doc 28 §4.10): formatting and the
 * fallback limits, shared by the tenant card (Secure Access → Security), the
 * Device Group form and the VPN user page. Values are seconds, as the API
 * stores them.
 */
import type { EffectiveSignIn, SignInLevel, SignInPolicyLimits, SignInSource } from '../services/access.service';

/** What the API serves with every view; used only until the first view arrives. */
export const FALLBACK_SIGN_IN_LIMITS: SignInPolicyLimits = {
  defaults: { reauthInterval: 36000, tokenLifetime: 900 },
  ranges: {
    reauthInterval: { min: 3600, max: 31536000 },
    tokenLifetime: { min: 300, max: 3600 },
  },
  presets: { reauthInterval: [36000, 86400, 604800, 2592000, 7776000] },
};

/** Token-lifetime choices offered besides "custom" (minutes, within 5–60). */
export const TOKEN_LIFETIME_PRESETS = [300, 600, 900, 1800, 3600];

/** "10 h", "24 h", "7 days", "30 days", "1 h 30 min", "15 min". */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0 && h === 0 && m === 0) return d === 1 ? '24 h' : `${d} days`;
  if (seconds >= 3600) {
    const totalH = Math.floor(seconds / 3600);
    return m ? `${totalH} h ${m} min` : `${totalH} h`;
  }
  return `${m} min`;
}

export const SOURCE_LABEL: Record<SignInSource, string> = {
  user: 'this user',
  group: 'device group',
  tenant: 'tenant',
  default: 'default',
};

/** "24 h (tenant)" */
export function describeValue(seconds: number, source: SignInSource): string {
  return `${formatDuration(seconds)} (${SOURCE_LABEL[source]})`;
}

/**
 * What applies at a level that sets `level`, given what applies when it sets
 * nothing (`inherited`) and the level's own source name. Pure.
 */
export function effectiveAt(level: SignInLevel, inherited: EffectiveSignIn, source: SignInSource): EffectiveSignIn {
  return {
    reauthInterval: level.reauthInterval ?? inherited.reauthInterval,
    reauthIntervalSource: level.reauthInterval !== null ? source : inherited.reauthIntervalSource,
    tokenLifetime: level.tokenLifetime ?? inherited.tokenLifetime,
    tokenLifetimeSource: level.tokenLifetime !== null ? source : inherited.tokenLifetimeSource,
  };
}

/** The defaults as an EffectiveSignIn (what a tenant with no values inherits). */
export function defaultsAsEffective(limits: SignInPolicyLimits): EffectiveSignIn {
  return {
    reauthInterval: limits.defaults.reauthInterval,
    reauthIntervalSource: 'default',
    tokenLifetime: limits.defaults.tokenLifetime,
    tokenLifetimeSource: 'default',
  };
}

/** The first problem with a level's values, or null — the API's own ranges. */
export function signInLevelError(level: SignInLevel, limits: SignInPolicyLimits): string | null {
  const r = limits.ranges;
  const { reauthInterval: ri, tokenLifetime: tl } = level;
  if (ri !== null && (!Number.isInteger(ri) || ri < r.reauthInterval.min || ri > r.reauthInterval.max)) {
    return `Re-authentication interval must be between ${formatDuration(r.reauthInterval.min)} and ${formatDuration(r.reauthInterval.max)}`;
  }
  if (tl !== null && (!Number.isInteger(tl) || tl < r.tokenLifetime.min || tl > r.tokenLifetime.max)) {
    return `Access-token lifetime must be between ${formatDuration(r.tokenLifetime.min)} and ${formatDuration(r.tokenLifetime.max)}`;
  }
  return null;
}

/** A group override as the API stores it: only the fields set. */
export function toGroupOverride(level: SignInLevel): { reauthInterval?: number; tokenLifetime?: number } {
  return {
    ...(level.reauthInterval !== null ? { reauthInterval: level.reauthInterval } : {}),
    ...(level.tokenLifetime !== null ? { tokenLifetime: level.tokenLifetime } : {}),
  };
}
